import { Drawer, Grid, Layout } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDesignSystem } from '../../../app/providers/ThemeProvider';
import WorkspaceShowcaseProvider from '../../../app/providers/WorkspaceShowcaseProvider';
import SidebarNav from './SidebarNav';
import Topbar from './Topbar';
import FlowAssistPanel from './FlowAssistPanel';
import CommandPalette from './CommandPalette';
import WorkspaceCopilot from '../../../features/copilot/components/WorkspaceCopilot';
import { CopilotPageContextProvider } from '../../../features/copilot/context/CopilotPageContext';
import { getFlowMeta } from './flowMeta';
import styles from './AppShell.module.css';

const { Content } = Layout;
const { useBreakpoint } = Grid;
const KEYBOARD_HINTS = [
  { key: 'Up / Down', label: 'Move focus' },
  { key: 'Enter', label: 'Select' },
  { key: 'Esc', label: 'Back' },
];
const PUBLIC_ROUTE_PREFIXES = [
  '/landing',
  '/login',
  '/auth/clerk/sign-in',
  '/accept-invitation',
  '/request-portal',
  '/email-preferences/unsubscribe',
  '/campaigns/unsubscribe',
  '/sample-portal',
  '/customer-portal',
  '/admin/login',
  '/admin',
];
const MOBILE_NAV_DRAWER_ID = 'app-navigation-drawer';

export default function AppShell({ children }) {
  const location = useLocation();
  const { showAssistPanel, uiCustomization } = useDesignSystem();
  const screens = useBreakpoint();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navigationToggleRef = useRef(null);
  const isKeyboardPath = location.pathname.startsWith('/keyboard');
  const isLandingRedirectRoot = location.pathname === '/';
  const isPublicPath = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  ) || isLandingRedirectRoot;
  const flowMeta = getFlowMeta(location.pathname);
  const showHelpPanel = !isKeyboardPath && !isPublicPath && showAssistPanel;
  const showDesktopNavigation = typeof screens.lg === 'boolean' ? screens.lg : true;

  useEffect(() => {
    setNavigationOpen(false);
  }, [location.pathname]);

  if (isPublicPath) {
    return (
      <Layout className={styles.rootPublic}>
        <Content className={styles.publicContent} id="app-main-content">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              data-testid="shell-content-inner"
              className={styles.publicInner}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Content>
      </Layout>
    );
  }

  return (
    <WorkspaceShowcaseProvider
      enabled={!isKeyboardPath}
      navigationOpen={navigationOpen}
      setNavigationOpen={setNavigationOpen}
      showDesktopNavigation={showDesktopNavigation}
    >
      <CopilotPageContextProvider>
        <Layout className={styles.root}>
          <a href="#app-main-content" className={styles.skipLink}>
            Skip to main content
          </a>
          {!isKeyboardPath && showDesktopNavigation && <SidebarNav />}
          <Layout>
            <Topbar
              showNavigationToggle={!isKeyboardPath && !showDesktopNavigation}
              onOpenNavigation={() => setNavigationOpen(true)}
              navigationOpen={navigationOpen}
              navigationDrawerId={MOBILE_NAV_DRAWER_ID}
              navigationToggleRef={navigationToggleRef}
            />
            <CommandPalette />
            <Content className={styles.content} id="app-main-content" tabIndex={-1}>
              {!isKeyboardPath && !showDesktopNavigation && (
                <Drawer
                  id={MOBILE_NAV_DRAWER_ID}
                  className={styles.navDrawer}
                  placement="left"
                  width="min(calc(100vw - 18px), var(--nav-mobile-drawer-width))"
                  title={null}
                  closable={false}
                  open={navigationOpen}
                  onClose={() => setNavigationOpen(false)}
                  afterOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                      navigationToggleRef.current?.focus?.();
                    }
                  }}
                  styles={{ body: { padding: 0 } }}
                  destroyOnClose
                >
                  <SidebarNav mobile onNavigate={() => setNavigationOpen(false)} />
                </Drawer>
              )}
              <div className={styles.contentMain}>
                {isKeyboardPath && (
                  <section className={styles.keyboardBanner} aria-label="Keyboard shortcuts">
                    <div>
                      <p className={styles.flowSection}>Keyboard Mode</p>
                      <h1 className={styles.keyboardTitle}>{flowMeta.title}</h1>
                      <p className={styles.flowSummary}>
                        Keep your hands on the keyboard. Use arrows to move, Enter to open, and Esc to go back.
                      </p>
                    </div>
                    <div className={styles.keyboardHintRow}>
                      {KEYBOARD_HINTS.map((hint) => (
                        <span key={hint.key} className={styles.keyboardHintPill}>
                          <span className={styles.keyboardKey}>{hint.key}</span>
                          <span>{hint.label}</span>
                        </span>
                      ))}
                    </div>
                  </section>
                )}
                {!isKeyboardPath && uiCustomization.showFlowBanner && (
                  <section className={styles.flowBanner} data-testid="flow-banner">
                    <div className={styles.flowCopy}>
                      <p className={styles.flowSection}>{flowMeta.section}</p>
                      <h1 className={styles.flowTitle}>{flowMeta.title}</h1>
                    </div>
                    <p className={styles.flowSummary}>{flowMeta.summary}</p>
                  </section>
                )}
                <div
                  className={
                    isKeyboardPath
                      ? styles.keyboardContentGrid
                      : showHelpPanel
                        ? styles.contentGrid
                        : styles.contentGridSingle
                  }
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={location.pathname}
                      data-testid="shell-content-inner"
                      className={styles.contentInner}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                    >
                      {children}
                    </motion.div>
                  </AnimatePresence>
                  {showHelpPanel && (
                    <aside className={styles.assistColumn}>
                      <FlowAssistPanel flowMeta={flowMeta} />
                    </aside>
                  )}
                </div>
              </div>
            </Content>
          </Layout>
          {!isKeyboardPath && <WorkspaceCopilot />}
        </Layout>
      </CopilotPageContextProvider>
    </WorkspaceShowcaseProvider>
  );
}
